import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../../utils/api'
import AdminLayout from '../components/AdminLayout';

interface Tag {
  id: number;
  name: string;
}

interface TagGroup {
  id: number;
  name: string;
  tags: Tag[];
}

interface BrandProfile {
  id: number;
  name: string;
  accountName: string;
  currentContentStrategyId?: number;
}

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  margin-bottom: 16px;
  color: #6b7280;
  a { color: #00a6c2; text-decoration: none; &:hover { text-decoration: underline; } }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #d97706;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: right;
  a { color: #00a6c2; text-decoration: none; font-size: 14px; &:hover { text-decoration: underline; } }
`;

const TabNav = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 24px;
`;

const TabLink = styled(Link)<{ $active?: boolean }>`
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ $active }) => ($active ? '#00a6c2' : '#6b7280')};
  text-decoration: none;
  border-bottom: 2px solid ${({ $active }) => ($active ? '#00a6c2' : 'transparent')};
  margin-bottom: -1px;
  &:hover { color: #00a6c2; background: #f9fafb; }
`;

const Section = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 24px;
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 20px 0;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 12px;
`;

const ExampleTitle = styled.h3`
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  margin: 0 0 12px 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 24px;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
`;

const Td = styled.td`
  padding: 10px 12px;
  font-size: 14px;
  color: #374151;
  border: 1px solid #e5e7eb;
`;

const ExampleTagLink = styled.span`
  color: #00a6c2;
`;

const TagGroupsContainer = styled.div`
  margin-top: 24px;
`;

const TagGroupLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
`;

const TagsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
`;

const TagBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  background: #e5e7eb;
  border-radius: 16px;
  font-size: 13px;
  color: #374151;
`;

const FileUploadSection = styled.div`
  margin-top: 24px;
  padding: 20px;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  text-align: center;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const FileSelectButton = styled.button`
  padding: 8px 16px;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  
  &:hover {
    background: #e5e7eb;
  }
`;

const FileName = styled.span`
  font-size: 14px;
  color: #374151;
  margin-left: 12px;
`;

const UploadButton = styled.button`
  padding: 10px 20px;
  background: #00a6c2;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  margin-top: 12px;
  &:hover { background: #0094ab; }
  &:disabled { background: #9ca3af; cursor: not-allowed; }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 48px;
  color: #6b7280;
`;

const LoadingText = styled.div`
  color: #6b7280;
  font-size: 14px;
  font-style: italic;
`;

function AdminPublicationBulkStoryTaggingPage() {
  const { id } = useParams<{ id: string }>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [brandProfile, setBrandProfile] = useState<BrandProfile | null>(null);
  const [tagGroups, setTagGroups] = useState<TagGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [tagGroupsLoading, setTagGroupsLoading] = useState(true);
  const [csvFile, setCsvFile] = useState<File | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get(`/admin2/publications/${id}/bulk_story_tagging/new.json`);
      setBrandProfile(res.data.brandProfile);
      setTagGroups(res.data.tagGroups || []);
      setTagGroupsLoading(false);
    } catch (err) {
      console.error('Failed to load data', err);
      setTagGroupsLoading(false);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const pubDisplayName = brandProfile ? `${brandProfile.accountName} - ${brandProfile.name}` : 'Publication';

  if (loading) {
    return (
      <AdminLayout 
        title="Publications" 
        breadcrumb={[
          { label: 'Publications', href: '/admin2/publications' },
          { label: 'Loading...' }
        ]}
      >
        <LoadingState>Loading...</LoadingState>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout 
      title="Publications" 
      breadcrumb={[
        { label: 'Publications', href: '/admin2/publications' },
        { label: pubDisplayName, href: `/admin2/publications/${id}/edit` },
        { label: 'Bulk Story Tagging' }
      ]}
    >
      <Breadcrumb>
        <Link to="/admin2/publications">Publications</Link>
        <span>›</span>
        <Link to={`/admin2/publications/${id}/edit`}>{brandProfile?.accountName} - {brandProfile?.name}</Link>
        <span>›</span>
        <span>Bulk story tagging</span>
        <span>›</span>
        <span>New</span>
      </Breadcrumb>

      <PageHeader>
        <PageTitle>Edit {brandProfile?.accountName} - {brandProfile?.name}</PageTitle>
        <HeaderActions>
          <a href={`/admin2/publications/${id}/talent_requests`}>Manage talent requests</a>
          {brandProfile?.currentContentStrategyId && (
            <Link to={`/admin2/content_strategies/${brandProfile.currentContentStrategyId}/edit`}>
              Manage content strategy
            </Link>
          )}
        </HeaderActions>
      </PageHeader>

      <TabNav>
        <TabLink to={`/admin2/publications/${id}/edit`}>Publication info</TabLink>
        <TabLink to={`/admin2/publications/${id}/users`}>Users</TabLink>
        <TabLink to={`/admin2/publications/${id}/transactions`}>Transactions</TabLink>
        <TabLink to={`/admin2/publications/${id}/fixed_arr_credits`}>Fixed ARR Credits</TabLink>
        <TabLink to={`/admin2/publications/${id}/bulk_story_tagging`} $active>Bulk Story Tagging</TabLink>
        <TabLink to={`/admin2/publications/${id}/talent_requests`}>Talent Requests</TabLink>
      </TabNav>

      <Section>
        <SectionTitle>Bulk Story Tagging</SectionTitle>

        <ExampleTitle>Example CSV</ExampleTitle>
        <Table>
          <thead>
            <tr>
              <Th>Title</Th>
              <Th>Id</Th>
              {tagGroups.slice(0, 2).map((tg, idx) => (
                <Th key={tg.id}>Tag Group {idx + 1}</Th>
              ))}
              {tagGroups.length < 2 && [...Array(2 - tagGroups.length)].map((_, idx) => (
                <Th key={`placeholder-${idx}`}>Tag Group {tagGroups.length + idx + 1}</Th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <Td>How to Upload Stuff!</Td>
              <Td>23145</Td>
              <Td>
                <ExampleTagLink>Example Tag Name 1, Another Example Tag Name 1</ExampleTagLink>
              </Td>
              <Td>
                <ExampleTagLink>Example Tag Name 2, Another Example Tag Name 2</ExampleTagLink>
              </Td>
            </tr>
          </tbody>
        </Table>

        <TagGroupsContainer>
          {tagGroupsLoading ? (
            <LoadingText>loading...</LoadingText>
          ) : tagGroups.length === 0 ? (
            <LoadingText>No tag groups configured for this publication.</LoadingText>
          ) : (
            tagGroups.map((group) => (
              <div key={group.id}>
                <TagGroupLabel>{group.name}</TagGroupLabel>
                <TagsList>
                  {group.tags.length === 0 ? (
                    <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>No tags in this group</span>
                  ) : (
                    group.tags.map((tag) => (
                      <TagBadge key={tag.id}>{tag.name}</TagBadge>
                    ))
                  )}
                </TagsList>
              </div>
            ))
          )}
        </TagGroupsContainer>

        <FileUploadSection>
          <h4 style={{ margin: '0 0 12px 0', color: '#374151' }}>Upload CSV File</h4>
          <p style={{ margin: '0 0 16px 0', color: '#6b7280', fontSize: '14px' }}>
            Upload a CSV file with story IDs and tags to bulk tag stories for this publication.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '16px 0' }}>
            <HiddenFileInput
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
            />
            <FileSelectButton
              type="button"
              onClick={() => fileInputRef.current?.click()}
            >
              Choose File
            </FileSelectButton>
            <FileName>
              {csvFile ? csvFile.name : 'No file chosen'}
            </FileName>
          </div>
          <UploadButton disabled>Upload and Process</UploadButton>
        </FileUploadSection>
      </Section>
    </AdminLayout>
  );
}

export default AdminPublicationBulkStoryTaggingPage;
